import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Check } from "lucide-react";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal } from "../components/Reveal";
import { PagePhoto } from "../components/PagePhoto";
import { pagePhotos } from "../content/photos";
import { CTASection } from "../components/CTASection";
import { business } from "../content/site";
import { trackEvent } from "../lib/analytics";
import { useLp } from "../i18n/lang";
import { useCopy } from "../i18n/copy";
import { useServices } from "../i18n/content";

const relatedServices = [
  "medicion-fincas-terrenos",
  "georreferenciacion-fincas",
  "gml-catastro-registro",
  "linderos-deslindes",
  "correccion-discrepancias-catastrales",
  "comprobacion-finca-antes-comprar",
];

export default function Prices() {
  const c = useCopy("prices");
  const cr = useCopy("crumbs");
  const cm = useCopy("common");
  const lp = useLp();
  const services = useServices();
  const crumbs = [
    { name: cr.home, path: lp("/") },
    { name: cr.prices, path: lp("/precios/") },
  ];
  return (
    <>
      <SEO
        title={c.seoT}
        description={c.seoD}
        path="/precios/"
        jsonLd={[breadcrumbSchema(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />
      <article className="container-site pb-8" data-testid="prices-page">
        <header className="pt-10 max-w-3xl">
          <p className="tech-label mb-4">{c.kicker}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            {c.h1}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            {c.intro}
          </p>
        </header>
        <PagePhoto photo={pagePhotos["_precios"]} className="mt-10 max-w-3xl" />

        <div className="max-w-3xl">
          <Reveal className="mt-12">
            <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-6">{c.factorsH}</h2>
            <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border hairline">
              {c.factors.map((f, i) => (
                <div key={f.title} className="bg-paper p-5">
                  <h3 className="text-[15px] font-semibold text-ink flex gap-2.5">
                    <span className="font-mono text-xs text-terracotta mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    {f.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[15px] text-slate-700 leading-relaxed">
              {c.factorsFoot}
            </p>
          </Reveal>

          <Reveal className="mt-12 border-t hairline pt-10">
            <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-4">{c.inclH}</h2>
            <p className="text-[15px] sm:text-base text-slate-700 leading-relaxed mb-4">
              {c.inclP1}
            </p>
            <p className="text-[15px] sm:text-base text-slate-700 leading-relaxed">
              {c.inclP2}
            </p>
          </Reveal>

          <Reveal className="mt-12 border-t hairline pt-10">
            <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-5">{c.needH}</h2>
            <ul className="space-y-3">
              {c.need.map((item, i) => (
                <li key={i} className="flex gap-3 text-[15px] text-slate-700 leading-relaxed">
                  <Check size={17} className="mt-1 shrink-0 text-precision" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 bg-paper-tint border hairline rounded-sm p-6">
              <p className="text-sm font-semibold text-ink mb-3">{c.exLead}</p>
              <ul className="space-y-2">
                {c.examples.map((m) => (
                  <li key={m} className="text-sm text-slate-600 italic">«{m}»</li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal className="mt-12 bg-obsidian text-paper rounded-sm p-8 sm:p-10" data-testid="prices-cta-block">
            <h2 className="text-xl sm:text-2xl font-semibold text-paper">{c.ctaH}</h2>
            <p className="mt-3 text-[15px] text-slate-300 leading-relaxed max-w-2xl">
              {c.ctaText}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={lp("/contacto/")} data-testid="prices-cta-budget"
                    onClick={() => trackEvent("cta_budget_click", { location: "precios" })} className="btn-primary">
                {c.ctaBudget} <ArrowRight size={15} aria-hidden="true" />
              </Link>
              <Link to={lp("/contacto/")} data-testid="prices-cta-refcat"
                    onClick={() => trackEvent("cta_budget_click", { location: "precios_refcat" })}
                    className="btn-secondary !border-paper/30 !text-paper hover:!bg-paper hover:!text-ink">
                {c.ctaRef}
              </Link>
              <a href={business.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="prices-cta-whatsapp"
                 onClick={() => trackEvent("whatsapp_click", { location: "precios" })}
                 className="btn-secondary !border-paper/30 !text-paper hover:!bg-paper hover:!text-ink">
                <MessageCircle size={15} aria-hidden="true" /> {cm.whatsapp}
              </a>
            </div>
          </Reveal>

          <Reveal className="mt-12">
            <h2 className="tech-label mb-4">{cm.relatedServices}</h2>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {relatedServices.map((s) => {
                const rel = services.find((x) => x.slug === s);
                return rel ? (
                  <li key={s}>
                    <Link to={lp(`/servicios/${s}/`)} className="link-underline text-[15px]">{rel.name}</Link>
                  </li>
                ) : null;
              })}
            </ul>
          </Reveal>
        </div>
      </article>
      <CTASection />
    </>
  );
}
